// A SEARCH BOX, not a chat. One line in, a filled-in form out.
//
// Deliberately has no message history, no assistant persona, no bubbles, no
// streaming and no typing indicator: this must read as a search field to a
// reviewer, because the 小程序 subject cannot hold the AI问答 service category.
// Everything below the input is a status line about the form, never a "reply".
//
// It is optional in the strongest sense — a user who never types here sees the
// same form, with the same behaviour, as before it existed.
//
// The AI-assisted label, the in-flight shimmer and the AI mark on the
// refinement note are WEB ONLY and come from AiAssist.jsx — see that file's
// header. The Mini Program renders this same box without any of them.

import { AiAssistLabel, AiInputShimmer, AiRefinedMark } from './AiAssist.jsx'

export default function SearchBox({
  value,
  onChange,
  onSubmit,
  busy,
  smartBusy,
  unreadable,
  unresolved,
  onDismissUnresolved,
  refined,
  onDismissRefined,
  ambiguous,
  onResolve,
  nameFor,
  memberNameFor,
  candidates,
  onPickAirport,
  airportLabel,
  focusNames
}) {
  return (
    <section className="mb-5 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <label className="block text-xs font-medium text-slate-500" htmlFor="nl-search">
          Describe your trip in your own words (optional)
        </label>
        <AiAssistLabel active={!!smartBusy} />
      </div>
      <form
        className="mt-1 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        {/* Relative wrapper only so the in-flight shimmer can sit over the
            input without touching its layout, focus ring or hit area. */}
        <div className="relative min-w-0 flex-1">
          <input
            id="nl-search"
            type="text"
            value={value}
            maxLength={500}
            autoComplete="off"
            onChange={(e) => onChange(e.target.value)}
            placeholder="Describe your trip: two of us, Italian and Chinese passports, from Shanghai"
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base"
          />
          {smartBusy && <AiInputShimmer />}
        </div>
        <button
          type="submit"
          disabled={!value.trim() || busy}
          className="shrink-0 rounded-lg bg-ocean px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
        >
          {busy ? 'Reading…' : 'Fill the form'}
        </button>
      </form>
      <p className="mt-1.5 text-[11px] text-slate-400">
        This only fills in the form below. You check it, then press search.
      </p>

      {unreadable && (
        <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Couldn’t read that, try filling the form below.
        </p>
      )}

      {/* Ambiguities block the search button until each one is answered. */}
      {ambiguous.map((a) => (
        <div
          key={a.matched}
          className="mt-3 rounded-lg border-l-4 border-horizon bg-horizon/10 px-3 py-2.5"
        >
          <p className="text-xs font-medium text-slate-700">
            {/* Name the traveller when the parser said whose nationality this
                is, so the user can see who the answer applies to. */}
            {Number.isInteger(a.person)
              ? a.codes.length === 2
                ? `${memberNameFor(a.person)} — ${nameFor(a.codes[0])} or ${nameFor(a.codes[1])}?`
                : `${memberNameFor(a.person)} — which “${a.matched}”?`
              : `Which “${a.matched}” did you mean?`}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {a.codes.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => onResolve(a.matched, code, a.person)}
                className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 active:bg-slate-50"
              >
                {nameFor(code)}
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* A departure city with more than one airport: offer, never guess. */}
      {candidates.length > 1 && (
        <div className="mt-3 rounded-lg border-l-4 border-horizon bg-horizon/10 px-3 py-2.5">
          <p className="text-xs font-medium text-slate-700">Which airport are you leaving from?</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {candidates.map((iata) => (
              <button
                key={iata}
                type="button"
                onClick={() => onPickAirport(iata)}
                className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 active:bg-slate-50"
              >
                <span className="font-mono">{iata}</span>
                {airportLabel(iata) && (
                  <span className="text-slate-500"> · {airportLabel(iata)}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Named, but not something the form can hold. Stated, never guessed at. */}
      {unresolved.length > 0 && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <span className="min-w-0">We didn’t recognise: {unresolved.join(', ')}.</span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={onDismissUnresolved}
            className="ml-auto shrink-0 font-semibold text-slate-400"
          >
            ×
          </button>
        </p>
      )}

      {focusNames.length > 0 && (
        <p className="mt-3 text-xs text-slate-500">
          We’ll highlight {focusNames.join(', ')} in the results.
        </p>
      )}

      {/* Stage 2 landed and changed something. Quiet, inline, non-modal. This
          is the ONLY place the UI says AI did anything, and it renders only
          when the refinement actually altered the form. */}
      {refined && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800">
          <AiRefinedMark />
          <span className="min-w-0">Updated your details with a closer reading.</span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={onDismissRefined}
            className="ml-auto shrink-0 font-semibold text-sky-400"
          >
            ×
          </button>
        </p>
      )}
    </section>
  )
}
